import { defineStore } from 'pinia'
import { config } from "@/core/config"
import { SmartSearchConfig } from "@/main"
import { searchLogger } from "@/core/Logger"

import { encodeUTF8ToBase64, decodeUTF8FromBase64 } from "@/core/utils"

export const usePopularProductsStore = defineStore('PopularProductsStore', {
  state: () => ({
    popularProducts: [] as any[],
    customPopularProduct: null as any,
  }),
  
  getters: {
    getTopProduct(state) {
      // Jeśli w configu jest zdefiniowany promotedProductId i mamy customPopularProduct
      // => użyj go, w przeciwnym razie popularProducts[0]
      return (
        window?._QONizer?.customSmartSearchConfig?.promotedProductId &&
        state.customPopularProduct
      )
        ? state.customPopularProduct
        : state.popularProducts[0]
    },
    
    getPopularProducts(state) {
      const limit = SmartSearchConfig.value?.onfocus?.popularProducts?.count ?? 4
      return state.popularProducts.slice(0, limit)
    },
    
    getPopularProductsWithoutFirst(state) {
      const limit = SmartSearchConfig.value?.onfocus?.popularProducts?.count ?? 4
      return state.popularProducts.slice(1, limit + 1)
    },
  },
  
  actions: {
    /**
     * Generuje klucz w sessionStorage np. "searchId_popularProducts".
     */
    getSessionStorageKey(): string {
      return `${config.searchId}_popularProducts`
    },
    
    /**
     * Metoda bazowa do pobrania listy produktów (lub custom product, gdy query != null).
     */
    async doFetch(query: string | null = null) {
      const data = new FormData()
      data.append('s', SmartSearchConfig.value.onfocus.popularProducts.sortName)
      if (query) {
        data.append('q', query)
      }
      return await fetch(
        `https://${config.apiHost}/restapi/${config.customerSymbol}/search/${config.searchId}/products`,
        {
          method: 'POST',
          headers: {
            'Api-key': config.apiKey,
          },
          credentials: 'include',
          body: data,
        }
      )
    },
    
    /**
     * Główna metoda: próbuje najpierw odczytać i rozkodować cache z sessionStorage.
     * Jeśli cokolwiek pójdzie źle (błąd parse, pusta tablica), przechodzi do fetch z API.
     */
    async fetchPopularProducts() {
      const sessionKey = this.getSessionStorageKey()
      
      // 1) Próbujemy wczytać z sessionStorage
      const base64data = sessionStorage.getItem(sessionKey)
      if (base64data) {
        try {
          // Dekodujemy z Base64 -> UTF-8
          const decoded = decodeUTF8FromBase64(base64data)
          // Parsujemy JSON
          const parsed = JSON.parse(decoded)
          
          // Spodziewamy się formy { popularProducts: [], customPopularProduct: ... }
          const { popularProducts, customPopularProduct } = parsed || {}
          
          // Jeśli popularProducts jest niepustą tablicą – użyjemy jej
          if (Array.isArray(popularProducts) && popularProducts.length > 0) {
            this.popularProducts = popularProducts
            this.customPopularProduct = customPopularProduct
            // Koniec, nie robimy fetch do API
            return
          }
          // W innym wypadku spadamy do fetch
        } catch (err) {
          // Błąd parse / dekodowania / cokolwiek => robimy fetch
          console.error('Error reading popular products cache:', err)
        }
      }
      
      // 2) Jeśli brak cache / błąd parse / pusta tablica => fetch z API
      try {
        // a) Pobierz popular products
        const popResp = await this.doFetch()
        let productsData: any[] = []
        if (popResp.ok) {
          const data = await popResp.json()
          productsData = data.products || []
        }
        this.popularProducts = productsData
        
        // b) Obsługa custom product (jeśli jest promotedProductId w configu)
        let customProd: any = null
        const promotedId = window?._QONizer?.customSmartSearchConfig?.promotedProductId
        if (promotedId) {
          try {
            searchLogger.log(`Getting Custom TopProduct: ${promotedId}`)
            const customResp = await this.doFetch(promotedId)
            if (customResp.ok) {
              const cdata = await customResp.json()
              const cproducts = cdata.products || []
              if (cproducts.length > 0) {
                customProd = cproducts[0]
                searchLogger.log('Received Custom Top Product', JSON.stringify(customProd))
              }
            }
          } catch (e) {
            console.error('Error retrieving custom TOP PRODUCT:', e)
          }
        }
        this.customPopularProduct = customProd
        
        // 3) Zapisujemy do sessionStorage w Base64
        const toStore = {
          popularProducts: this.popularProducts,
          customPopularProduct: this.customPopularProduct,
        }
        const stringified = JSON.stringify(toStore)
        const encoded = encodeUTF8ToBase64(stringified)
        sessionStorage.setItem(sessionKey, encoded)
        
      } catch (error) {
        console.error('Error retrieving popular products:', error)
      }
    },
    
    /**
     * Czyści cache i store – np. w celu wymuszenia ponownego pobrania
     */
    clearPopularProductsCache() {
      const sessionKey = this.getSessionStorageKey()
      sessionStorage.removeItem(sessionKey)
      this.popularProducts = []
      this.customPopularProduct = null
    },
  },
})
